import React, { useEffect, useRef, useState } from "react";
import { Button, Card, CardBody, Input, Spinner } from "reactstrap";

import SectionCard from "../SectionCard";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotInsetCardStyle,
  autopilotSecondaryButtonStyle,
} from "../autopilot/styles";

import "./styles.css";

/**
 * Faixa de busca + botão "Filtros" PADRÃO do Hub (PO1-2724), extraída da
 * Auditoria de Faturas (.conf-fatura-busca) e da Auditoria de CT-e.
 *
 * Dois modos:
 * - `client` (default): busca instantânea; o chamador filtra em memória a cada
 *   `onSearchChange`. Sem botões Aplicar/Limpar (a menos que `showActions`).
 * - `server`: `onSearchChange` continua a cada tecla (input controlado), mas o
 *   disparo `onSearchSubmit` é debounced (350 ms) e Enter dispara na hora; o
 *   painel ganha rodapé com "Aplicar filtros" (`onApply`) e "Limpar" (`onClear`).
 *
 * O painel avançado (children) fica dentro de um SectionCard e é escondido com
 * display:none quando fechado, preservando o estado dos campos.
 *
 * Props principais: mode, searchLabel, searchHint, searchPlaceholder, searchValue,
 * onSearchChange, onSearchSubmit, debounceMs, activeFiltersCount, open,
 * defaultOpen, onToggle, panelTitle, panelDescription, extra (slot na faixa),
 * onApply, onClear, busy, withCard (true = Card.card-everlog-shell próprio, como
 * nas telas de referência; false = só faixa + painel, para montar num card já
 * existente), children.
 */
const panelCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "1rem 1rem 0.95rem",
  marginBottom: "1rem",
};

let panelIdSeq = 0;

export default function FilterBand({
  mode = "client",
  searchLabel = "Buscar",
  searchHint,
  searchPlaceholder = "Buscar...",
  searchType = "search",
  searchValue = "",
  onSearchChange,
  onSearchSubmit,
  debounceMs = 350,
  searchDisabled = false,
  activeFiltersCount = 0,
  open,
  defaultOpen = false,
  onToggle,
  filtersLabel = "Filtros",
  panelTitle = "Filtros",
  panelDescription,
  extra,
  onApply,
  onClear,
  applyLabel = "Aplicar filtros",
  clearLabel = "Limpar",
  busy = false,
  showActions,
  showFiltersButton,
  withCard = true,
  className = "",
  style,
  cardStyle,
  panelStyle,
  children,
}) {
  const isServer = mode === "server";
  const controlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(Boolean(defaultOpen));
  const isOpen = controlled ? Boolean(open) : internalOpen;

  const timerRef = useRef(null);
  const panelIdRef = useRef(null);
  if (panelIdRef.current === null) {
    panelIdSeq += 1;
    panelIdRef.current = `hub-fb-panel-${panelIdSeq}`;
  }

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const cancelPending = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleChange = (event) => {
    const value = event.target.value;
    if (onSearchChange) onSearchChange(value);
    if (!isServer || !onSearchSubmit) return;
    cancelPending();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onSearchSubmit(value);
    }, debounceMs);
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" || !isServer || !onSearchSubmit) return;
    event.preventDefault();
    cancelPending();
    onSearchSubmit(event.target.value);
  };

  const toggle = () => {
    const next = !isOpen;
    if (!controlled) setInternalOpen(next);
    if (onToggle) onToggle(next);
  };

  const hasPanel = Boolean(children);
  // O botão "Filtros" aparece quando há painel interno; `showFiltersButton`
  // força/oculta (ex.: tela que gerencia o painel fora do FilterBand via open/onToggle).
  const showToggle = showFiltersButton === undefined ? hasPanel : Boolean(showFiltersButton);
  const renderActions = (showActions === undefined ? isServer : showActions) && (onApply || onClear);

  const band = (
    <>
      <div className="hub-fb">
        <div className="hub-fb__label">
          <span className="hub-fb__title">{searchLabel}</span>
          {searchHint ? <span className="hub-fb__hint">{searchHint}</span> : null}
        </div>
        <div className="hub-fb__field">
          <i className="tim-icons icon-zoom-split hub-fb__icon" />
          <Input
            type={searchType}
            className="hub-fb__input"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={searchDisabled}
            aria-label={searchLabel}
          />
        </div>
        {showToggle || extra ? (
          <div className="hub-fb__right">
            {extra}
            {showToggle ? (
              <button
                type="button"
                className={`hub-fb__filtros${isOpen ? " is-open" : ""}`}
                onClick={toggle}
                aria-expanded={isOpen}
                aria-controls={hasPanel ? panelIdRef.current : undefined}
              >
                <i className="tim-icons icon-settings-gear-63" />
                <span>{filtersLabel}</span>
                {activeFiltersCount > 0 ? (
                  <span className="hub-fb__filtros-badge">{activeFiltersCount}</span>
                ) : null}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {hasPanel ? (
        <div
          id={panelIdRef.current}
          className="hub-fb__panel"
          style={{ display: isOpen ? "block" : "none", ...panelStyle }}
        >
          <SectionCard title={panelTitle} description={panelDescription} style={panelCardStyle}>
            {children}
            {renderActions ? (
              <div className="hub-fb__actions">
                {onApply ? (
                  <Button size="sm" style={autopilotSecondaryButtonStyle} onClick={onApply} disabled={busy}>
                    {busy ? <Spinner size="sm" /> : applyLabel}
                  </Button>
                ) : null}
                {onClear ? (
                  <Button size="sm" color="default" outline onClick={onClear} disabled={busy}>
                    {clearLabel}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </SectionCard>
        </div>
      ) : null}
    </>
  );

  if (!withCard) {
    return (
      <div className={`hub-fb-root ${className}`.trim()} style={style}>
        {band}
      </div>
    );
  }

  return (
    <Card
      className={`card-everlog-shell hub-fb-card ${className}`.trim()}
      style={{
        ...autopilotCardStyle,
        marginTop: "1.25rem",
        background: "var(--everlog-card-hero-bg)",
        ...cardStyle,
        ...style,
      }}
    >
      <CardBody style={autopilotBodyStyle}>{band}</CardBody>
    </Card>
  );
}
